import { isNativeWebView } from '@/common/utils/isNativeWebView';
import { isPWA } from '@/common/utils/isPwa';
import { useSearch } from '@/modules/search/hooks/useSearch';
import { getCookie } from 'cookies-next';
import { useSearchRouter } from '@/modules/search/hooks/useSearchRouter';
import getConfig from 'next/config';
import { ReactNode } from 'react';
import {
  LANDING_HOSTS,
  LANDING_PREVIEW_COOKIE,
  LANDING_PREVIEW_DAYS,
  LANDING_PREVIEW_PARAM,
  applyPreviewParam,
  isLandingHost,
  isLandingPreview,
  isNationalTemplate,
  landingTemplate,
  landingVariant,
  parseLandingPercent,
} from './landingVariant';

const { publicRuntimeConfig } = getConfig() ?? { publicRuntimeConfig: {} };

/** Set on <html> while the blocks are (about to be) shown; the slot below is visible only then. */
export const LANDING_HTML_ATTR = 'data-dr56';

/**
 * Runs while the HTML is parsed, before the first paint: the same decision as `landingVariant` (preview
 * cookie, app, terminal hash below the share), so the slot keeps the blocks' room from the start and the
 * specialty list does not jump when they arrive after hydration (CLS 0.5–0.7 without it, 2026-10-28).
 * The blocks themselves stay client-only: the CDN shares this HTML between both arms.
 */
export const landingReserveScript = (percent: number, national = false): string =>
  // `noModule` = a browser that also runs the Next bundles; an older one would keep the room empty forever.
  `(function(){try{if(!('noModule' in HTMLScriptElement.prototype))return;if(${JSON.stringify(
    LANDING_HOSTS,
  )}.indexOf(location.hostname)<0)return;` +
  // ?dr56=1|0 from a shared link sets or clears the preview cookie first (landingVariant.previewCookieFor).
  `var p=location.search.match(/[?&]${LANDING_PREVIEW_PARAM}=([01])(?:&|$)/);if(p)document.cookie='${LANDING_PREVIEW_COOKIE}='+(p[1]==='1'?'1; path=/; max-age=${
    LANDING_PREVIEW_DAYS * 86400
  }':'; path=/; max-age=0')+'; samesite=lax';var c=document.cookie,g=function(n){var m=c.match(new RegExp('(?:^|; )'+n+'=([^;]*)'));return m?decodeURIComponent(m[1]):null};` +
  // All Iran: a saved city (or «اطراف من») is sent on to that city by the page, never shown this landing.
  (national ? `if(g('new-city'))return;` : '') +
  `var on=g('${LANDING_PREVIEW_COOKIE}')==='1';if(!on){var ua=navigator.userAgent.toLowerCase(),` +
  `pwa=matchMedia('(display-mode: standalone)').matches||document.referrer.indexOf('android-app://')>-1||document.referrer.indexOf('ios-app://')>-1,` +
  `wv=!pwa&&((ua.indexOf('android')>-1&&ua.indexOf('wv')>-1)||(ua.indexOf('iphone')>-1&&ua.indexOf('safari')<0)),t=g('terminal_id');` +
  `if(!pwa&&!wv&&t){var h=0x811c9dc5,s='dr56:'+t;for(var i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,0x01000193);}on=(h>>>0)%100<${percent};}}` +
  `if(on)document.documentElement.setAttribute('${LANDING_HTML_ATTR}','');}catch(e){}})();`;

/**
 * Is the old list this element sits in hidden by the new landing (`[data-dr56-hide]` under `html[data-dr56]`, the CSS
 * below)? Its cards are then not on screen, so the page sends no card view (impression) for them: they would count
 * as seen and inflate the impressions and the CTR denominator of the arm.
 * `html[data-dr56]` is set by the pre-paint script on a page load, but on a client-side visit only once the landing's
 * own code has loaded, which can be after the search's events; so without it the same decision is taken here (the
 * pre-paint script's: host, the saved city of all Iran, preview cookie, app, terminal hash below the share).
 */
export const listHiddenByLanding = (el: Element | null | undefined, national = false): boolean => {
  if (typeof window === 'undefined' || !el?.closest('[data-dr56-hide]')) return false;
  if (document.documentElement.hasAttribute(LANDING_HTML_ATTR)) return true;
  if (!isLandingHost(window.location.hostname)) return false;
  applyPreviewParam();
  if (national && getCookie('new-city')) return false;
  const arm = landingVariant({
    terminalId: getCookie('terminal_id') as string | undefined,
    percent: parseLandingPercent(publicRuntimeConfig?.SEARCH_LANDING_V0_PERCENT),
    isApp: isPWA() || isNativeWebView(),
    preview: isLandingPreview(getCookie(LANDING_PREVIEW_COOKIE)),
  });
  return arm === 'v0' || arm === 'preview';
};

/**
 * Measured on a local build against the live API (2026-10-30, v1.6): everything above the first doctor row (quick
 * actions, the one-row symptom strip and the specialty tile strip, with the spaces between them) is 390–406px from
 * 360px up and 426px at 320px where labels wrap; plus the first doctor row's fixed room (23.25rem: a card with its
 * online-price line, landingRows). The slot holds the whole landing, so a larger room than the screen costs nothing.
 */
const FIRST_ROW = '23.25rem';
/** The trust line under the quick actions (one 20px line and the 16px gap above it): online since v1.8, every landing since v2.0. */
const TRUST_LINE = '36px';
/** v1.9: the city box of the general nationwide landing (title, one row of chips, padding, and the gap above it). */
const CITIES_BOX = '124px';
const RESERVE_CSS = `
html[${LANDING_HTML_ATTR}] [data-dr56-hide]{display:none!important}
[data-dr56-slot]{display:none}
html[${LANDING_HTML_ATTR}] [data-dr56-slot]{display:block!important;margin-bottom:.75rem;min-height:calc(406px + ${TRUST_LINE} + ${FIRST_ROW})}
html[${LANDING_HTML_ATTR}] [data-dr56-slot=general_ir]{min-height:calc(406px + ${TRUST_LINE} + ${CITIES_BOX} + ${FIRST_ROW})}
@media (max-width:340px){html[${LANDING_HTML_ATTR}] [data-dr56-slot]{min-height:calc(426px + ${TRUST_LINE} + ${FIRST_ROW})}html[${LANDING_HTML_ATTR}] [data-dr56-slot=general_ir]{min-height:calc(426px + ${TRUST_LINE} + ${CITIES_BOX} + ${FIRST_ROW})}}
`;

/**
 * The landing of this address as the server can know it (no cookie, no store): `landingTemplate` without the saved
 * city. The page uses it to keep the room and to mark what the arm hides; the browser decides the rest.
 */
export const useLandingPage = () => {
  const { isLanding, isLoading } = useSearch();
  const {
    query: { params, ...query },
  } = useSearchRouter();
  return { template: landingTemplate({ params, query, isLanding }), isLoading };
};

/** Server-rendered room for the top blocks on /s/<city>/ (with or without ?turn_type=consult) and on all Iran. */
export const LandingV0Slot = ({ children }: { children: ReactNode }) => {
  const { template, isLoading } = useLandingPage();
  if (!template) return <>{children}</>;
  return (
    // `hidden` keeps it out of the parent's space-y spacing for everyone outside the arm. While a client-side
    // search loads, `isLanding` is only a guess and the blocks are not drawn: no room then (a quick action like
    // «نوبت امروز» would show an empty box, then jump when the results arrive). The element itself stays, so
    // the blocks are not remounted when the same landing reloads.
    <div hidden data-dr56-slot={isLoading ? undefined : template}>
      <style dangerouslySetInnerHTML={{ __html: RESERVE_CSS }} />
      <script
        dangerouslySetInnerHTML={{
          __html: landingReserveScript(parseLandingPercent(publicRuntimeConfig?.SEARCH_LANDING_V0_PERCENT), isNationalTemplate(template)),
        }}
      />
      {children}
    </div>
  );
};
